import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import SmsIcon from "@mui/icons-material/Sms";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { toast } from "react-toastify";

import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  getCommunicationSettings,
  updateCommunicationSettings,
} from "services/communicationSettingService";

const defaultSettings = {
  smsPaused: false,
  whatsappPaused: false,
  reason: "",
  envOverride: {},
  updatedAt: "",
};

function MessageControls() {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const applySettings = (data = {}) => {
    setSettings({
      smsPaused: Boolean(data.smsPaused),
      whatsappPaused: Boolean(data.whatsappPaused),
      reason: data.reason || "",
      envOverride: data.envOverride || {},
      updatedAt: data.updatedAt || "",
    });
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getCommunicationSettings();
      if (response.success) applySettings(response.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch message controls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateCommunicationSettings({
        smsPaused: settings.smsPaused,
        whatsappPaused: settings.whatsappPaused,
        reason: settings.reason,
      });
      if (response.success) {
        applySettings(response.data);
        toast.success("Message controls updated successfully");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update message controls");
    } finally {
      setSaving(false);
    }
  };

  const envLockActive =
    settings.envOverride?.allPaused ||
    settings.envOverride?.smsPaused ||
    settings.envOverride?.whatsappPaused;
  const formattedUpdatedAt = settings.updatedAt
    ? new Date(settings.updatedAt).toLocaleString()
    : "Not saved yet";

  const channelCards = [
    {
      key: "smsPaused",
      title: "Other SMS",
      icon: <SmsIcon color={settings.smsPaused ? "error" : "success"} sx={{ fontSize: 34 }} />,
      paused: settings.smsPaused,
      envPaused: settings.envOverride?.allPaused || settings.envOverride?.smsPaused,
      helper: "Login OTP SMS will keep running",
    },
    {
      key: "whatsappPaused",
      title: "WhatsApp",
      icon: (
        <WhatsAppIcon color={settings.whatsappPaused ? "error" : "success"} sx={{ fontSize: 34 }} />
      ),
      paused: settings.whatsappPaused,
      envPaused: settings.envOverride?.allPaused || settings.envOverride?.whatsappPaused,
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox
              display="flex"
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              mb={3}
              pb={2}
              borderBottom="1px solid #e9ecef"
            >
              <ArgonBox display="flex" alignItems="center" gap={1.5}>
                <SettingsSuggestIcon color="primary" sx={{ fontSize: 34 }} />
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="medium">
                    Message Controls
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="text">
                    Last updated: {formattedUpdatedAt}
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <ArgonButton
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon fontSize="small" />}
                &nbsp;Save
              </ArgonButton>
            </ArgonBox>

            {loading ? (
              <ArgonBox textAlign="center" py={5}>
                <CircularProgress color="primary" />
                <ArgonTypography variant="body2" color="text" mt={2}>
                  Loading message controls...
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <>
                {envLockActive && (
                  <ArgonBox
                    mb={2}
                    p={2}
                    borderRadius="8px"
                    sx={{ backgroundColor: "#fff7e6", border: "1px solid #ffd591" }}
                  >
                    <ArgonTypography variant="button" color="warning" fontWeight="bold">
                      Environment pause is active. Server env value will keep selected channels paused.
                    </ArgonTypography>
                  </ArgonBox>
                )}

                <Grid container spacing={3}>
                  {channelCards.map((channel) => (
                    <Grid item xs={12} md={6} key={channel.key}>
                      <ArgonBox
                        p={2.5}
                        borderRadius="8px"
                        sx={{
                          border: "1px solid #e9ecef",
                          backgroundColor: channel.paused ? "#fff5f5" : "#f6fffb",
                        }}
                      >
                        <ArgonBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                        >
                          <ArgonBox display="flex" alignItems="center" gap={1.5}>
                            {channel.icon}
                            <ArgonBox>
                              <ArgonTypography variant="h6" fontWeight="medium">
                                {channel.title}
                              </ArgonTypography>
                              <ArgonTypography
                                variant="caption"
                                color={channel.paused ? "error" : "success"}
                                fontWeight="bold"
                              >
                                {channel.paused ? "Paused" : "Running"}
                                {channel.envPaused ? " by env" : ""}
                              </ArgonTypography>
                              {channel.helper && (
                                <ArgonTypography variant="caption" color="text" display="block">
                                  {channel.helper}
                                </ArgonTypography>
                              )}
                            </ArgonBox>
                          </ArgonBox>
                          <Switch
                            checked={!channel.paused}
                            onChange={(event) => handleChange(channel.key, !event.target.checked)}
                            disabled={saving}
                          />
                        </ArgonBox>
                      </ArgonBox>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" fontWeight="bold">
                      Pause Reason
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      value={settings.reason}
                      placeholder="Optional note for why messages are paused"
                      onChange={(event) => handleChange("reason", event.target.value)}
                      disabled={saving}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MessageControls;
